import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Empty,
  Input,
  InputNumber,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import {
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { extractApiErrorMessage } from '@/api/client';
import { isHiddenCommunity, listCommunities, listStudents } from '@/api/students';
import { useDebounce } from '@/hooks/useDebounce';
import {
  COMMUNITY_MEMBER_STATUS_LABELS,
  ROLE_COMMUNITY_LABELS,
  type CommunityMemberStatus,
  type StudentMembership,
  type StudentSummary,
  type StudentListParams,
} from '@/types/students';
import { StudentDetailDrawer } from './StudentDetailDrawer';

const { Title, Text } = Typography;

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];

const ENV_DEFAULT_COMMUNITY_ID = Number(
  import.meta.env.VITE_DEFAULT_COMMUNITY_ID ?? '0',
);

const STATUS_COLOR: Record<CommunityMemberStatus, string> = {
  PENDING: 'gold',
  APPROVED: 'green',
  CANCELLING: 'orange',
  CHURNED: 'default',
  BANNED: 'red',
  REJECTED: 'volcano',
};

interface ParsedFilters {
  communityId?: number;
  page: number;
  size: number;
  sort: string;
  search: string;
  cohort?: number;
}

function parseSearchParams(params: URLSearchParams): ParsedFilters {
  const pageRaw = Number(params.get('page') ?? '0');
  const sizeRaw = Number(params.get('size') ?? String(DEFAULT_PAGE_SIZE));
  const cohortRaw = params.get('cohort');
  const communityIdRaw = params.get('communityId');
  // URL takes priority; if absent, fall back to env default ONLY when explicitly set (> 0).
  const initialCommunityId =
    communityIdRaw !== null
      ? Number(communityIdRaw)
      : ENV_DEFAULT_COMMUNITY_ID > 0
        ? ENV_DEFAULT_COMMUNITY_ID
        : undefined;
  return {
    communityId:
      initialCommunityId &&
      Number.isFinite(initialCommunityId) &&
      initialCommunityId > 0
        ? initialCommunityId
        : undefined,
    page: Number.isFinite(pageRaw) && pageRaw >= 0 ? pageRaw : 0,
    size: Number.isFinite(sizeRaw) && sizeRaw > 0 ? sizeRaw : DEFAULT_PAGE_SIZE,
    sort: params.get('sort') ?? 'createdAt,desc',
    search: params.get('search') ?? '',
    cohort: cohortRaw ? Number(cohortRaw) : undefined,
  };
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : '—';
}

function membershipTooltip(m: StudentMembership): string {
  const parts: string[] = [];
  if (m.communityRole) parts.push(ROLE_COMMUNITY_LABELS[m.communityRole]);
  if (m.membershipStatus) parts.push(COMMUNITY_MEMBER_STATUS_LABELS[m.membershipStatus]);
  if (m.cohort) parts.push(`K${m.cohort}`);
  if (m.joinedAt) parts.push(`Tham gia: ${formatDateTime(m.joinedAt)}`);
  return parts.join(' · ');
}

export function StudentListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseSearchParams(searchParams), [searchParams]);

  const [searchInput, setSearchInput] = useState(filters.search);
  const debouncedSearch = useDebounce(searchInput, 350);

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  useEffect(() => {
    if (debouncedSearch === filters.search) return;
    const next = new URLSearchParams(searchParams);
    if (debouncedSearch) next.set('search', debouncedSearch);
    else next.delete('search');
    next.delete('page');
    setSearchParams(next, { replace: true });
  }, [debouncedSearch, filters.search, searchParams, setSearchParams]);

  const queryParams: StudentListParams = {
    communityId: filters.communityId,
    page: filters.page,
    size: filters.size,
    sort: filters.sort,
    search: filters.search || undefined,
    cohort: filters.cohort,
  };

  const query = useQuery({
    queryKey: ['students', queryParams],
    queryFn: () => listStudents(queryParams),
    placeholderData: keepPreviousData,
  });

  const communitiesQuery = useQuery({
    queryKey: ['admin-communities'],
    queryFn: listCommunities,
    staleTime: 5 * 60_000,
  });

  const communityOptions = (communitiesQuery.data ?? []).map((c) => ({
    value: c.id,
    label: c.name ?? `Community #${c.id}`,
  }));

  const updateParam = (
    patch: Partial<Record<string, string | undefined>>,
    resetPage = true,
  ) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === '' || value === null) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    if (resetPage) next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<StudentSummary> | SorterResult<StudentSummary>[],
  ) => {
    const next = new URLSearchParams(searchParams);
    const current = pagination.current ?? 1;
    const pageSize = pagination.pageSize ?? DEFAULT_PAGE_SIZE;
    next.set('page', String(Math.max(0, current - 1)));
    next.set('size', String(pageSize));

    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    if (singleSorter?.field && singleSorter.order) {
      const direction = singleSorter.order === 'ascend' ? 'asc' : 'desc';
      next.set('sort', `${String(singleSorter.field)},${direction}`);
    }

    setSearchParams(next, { replace: true });
  };

  const columns: ColumnsType<StudentSummary> = [
    {
      title: 'Học viên',
      key: 'student',
      width: 260,
      render: (_, row) => (
        <Space>
          <Avatar
            src={row.avatarUrl ?? undefined}
            icon={<UserOutlined />}
            size={36}
          />
          <Space direction="vertical" size={0}>
            <Text strong>{row.fullname ?? '—'}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ID #{row.userId}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      ellipsis: true,
      width: 200,
      render: (value: string | null) => value ?? '—',
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'mobile',
      key: 'mobile',
      sorter: true,
      width: 140,
      render: (value: string | null) => value ?? '—',
    },
    {
      title: 'Cộng đồng',
      key: 'communities',
      render: (_, row) => {
        const visible = row.memberships.filter((m) => !isHiddenCommunity(m.communityId));
        if (!visible.length) return '—';
        return (
          <Space size={[8, 4]} wrap>
            {visible.map((m) => {
              const label = m.communityName ?? `#${m.communityId}`;
              const statusColor = m.membershipStatus
                ? STATUS_COLOR[m.membershipStatus]
                : 'default';
              const hasCohort = m.cohort !== null && m.cohort !== undefined;
              return (
                <Tooltip key={m.membershipId} title={membershipTooltip(m)}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Tag color={statusColor} style={{ marginInlineEnd: 0 }}>
                      {label}
                    </Tag>
                    {hasCohort ? (
                      <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
                        K{m.cohort}
                      </Tag>
                    ) : null}
                  </span>
                </Tooltip>
              );
            })}
          </Space>
        );
      },
    },
  ];

  const sortField = filters.sort.split(',')[0];
  const sortOrder = filters.sort.split(',')[1];

  const columnsWithSort = columns.map((col) => {
    if (!col.sorter || !('dataIndex' in col)) return col;
    const field = String(col.dataIndex);
    if (field === sortField) {
      return {
        ...col,
        sortOrder: (sortOrder === 'asc' ? 'ascend' : 'descend') as
          | 'ascend'
          | 'descend',
      };
    }
    return col;
  });

  const pageData = query.data;
  const errorMessage = query.error
    ? extractApiErrorMessage(query.error, 'Không tải được danh sách học viên.')
    : null;

  return (
    <div className="student-list">
      <div className="student-list-header">
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Học viên
          </Title>
          <Text type="secondary">
            {pageData
              ? `${pageData.totalElements.toLocaleString('vi-VN')} học viên${(() => {
                  if (!filters.communityId) return ' · tất cả cộng đồng';
                  const opt = communityOptions.find(
                    (o) => o.value === filters.communityId,
                  );
                  return opt
                    ? ` · ${opt.label}`
                    : ` · community #${filters.communityId}`;
                })()}`
              : 'Đang tải…'}
          </Text>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => query.refetch()}
          loading={query.isFetching}
        >
          Tải lại
        </Button>
      </div>

      <Card variant="borderless" className="filter-card">
        <Space wrap size="middle" style={{ width: '100%' }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Tìm theo tên / email / SĐT"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ width: 260 }}
          />

          <InputNumber
            min={1}
            placeholder="Khoá"
            value={filters.cohort ?? undefined}
            onChange={(value) =>
              updateParam({
                cohort: value === null || value === undefined ? undefined : String(value),
              })
            }
            style={{ width: 120 }}
          />
        </Space>
      </Card>

      {errorMessage ? (
        <Alert
          type="error"
          showIcon
          message="Lỗi"
          description={errorMessage}
          style={{ marginTop: 16 }}
        />
      ) : null}

      <Card variant="borderless" className="table-card">
        <Table<StudentSummary>
          rowKey="userId"
          columns={columnsWithSort}
          dataSource={pageData?.content ?? []}
          loading={query.isFetching}
          locale={{ emptyText: <Empty description="Không có học viên nào" /> }}
          pagination={{
            current: filters.page + 1,
            pageSize: filters.size,
            total: pageData?.totalElements ?? 0,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            showTotal: (total, range) =>
              `${range[0]}–${range[1]} / ${total.toLocaleString('vi-VN')}`,
          }}
          onChange={handleTableChange}
          onRow={(row) => ({
            onClick: () => setSelectedUserId(row.userId),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <StudentDetailDrawer
        userId={selectedUserId}
        open={selectedUserId !== null}
        onClose={() => setSelectedUserId(null)}
      />
    </div>
  );
}
